// The pure half of the consented browser tracker: what it is asked to send,
// what it is never allowed to send, and the addresses it is served from.
//
// Split out from the module that loads the script so every rule here is a table
// in the unit suite rather than something only a browser can answer. Browser-
// safe: no framework import, no Node built-in.

/**
 * The first-party prefix everything the measurement tool serves to a browser
 * lives under.
 *
 * Not the tool's own default paths, and not a subdomain named after it: filter
 * lists carry domain-agnostic rules that match `/script.js` and `/api/send` on
 * ANY host, and a hostname containing the tool's name is pattern-matched by
 * dozens of them. One prefix, one upstream — the reverse proxy maps exactly the
 * four addresses below onto the container and refuses everything else under it.
 */
export const MEASUREMENT_PREFIX = "/s";

/** The tracker, under the name the container is configured to serve it as. */
export const TRACKER_SRC = `${MEASUREMENT_PREFIX}/t.js`;

/** The session recorder. The container serves it at its own default name. */
export const RECORDER_SRC = `${MEASUREMENT_PREFIX}/recorder.js`;

/**
 * Where the tracker posts, and where the recorder posts — two different values,
 * because the two scripts build their endpoints differently and one of them is
 * renamed by the deployment.
 *
 * The tracker appends the collect path to whatever `data-host-url` says, and
 * the container has rewritten that path inside the script it serves: with the
 * endpoint renamed to `/s/e`, the tracker computes `<host-url> + "/s/e"`. So
 * `data-host-url` has to be the ORIGIN ROOT and not the prefix — `"/s"` would
 * produce `/s/s/e`, and leaving it out is worse, because the script then falls
 * back to its own directory and produces the same wrong address silently.
 *
 * The recorder appends `/api/record` and `/api/websites/<id>/recorder`, neither
 * of which the rename touches, so it is given the prefix and the proxy strips
 * it. Both values are asserted against the pinned script in the unit suite.
 */
export const TRACKER_HOST_URL = "/";
export const RECORDER_HOST_URL = MEASUREMENT_PREFIX;

/** What the recorder posts a batch of frames to, as the browser addresses it. */
export const RECORDER_ENDPOINT_PREFIX = `${MEASUREMENT_PREFIX}/api/`;

/**
 * Whether an address is one the recorder talks to — its frame sink and the
 * settings it reads before it starts.
 *
 * Used by the transport guard, so it answers for a relative address, an
 * absolute one on this origin and a `URL`'s own string alike; anything it
 * cannot parse is NOT the recorder's, because the recorder only ever builds
 * these two from a prefix this module states.
 */
export function isRecorderEndpoint(rawUrl: unknown): boolean {
  if (typeof rawUrl !== "string" || rawUrl === "") return false;
  let path: string;
  try {
    path = new URL(rawUrl, "http://localhost").pathname;
  } catch {
    return false;
  }
  return path.startsWith(RECORDER_ENDPOINT_PREFIX);
}

/**
 * Campaign parameters are the reason a query is carried into a measurement row
 * at all. Everything else is cut, by the same rule the origin access log
 * already follows — a record read by more people for longer than the request
 * lasted is held to the log's own standard, and a browser has no idea which of
 * its query parameters is a one-time credential.
 */
export const KEPT_QUERY_PARAMETERS: readonly string[] = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
  "ref",
];

/** Addresses whose query is cut in full, credential-shaped or not. */
const QUERY_STRIPPED_PREFIXES: readonly string[] = [
  "/login",
  "/register",
  "/verify",
  "/forgot-password",
  "/reset-password",
  "/auth",
  "/api",
];

/**
 * A page address reduced to what a measurement row may hold: a path, and only
 * the campaign parameters of its query.
 *
 * This is also the fix for the defect the tool has on this framework. Its
 * tracker resolves every address it reports against `location.href`, so a
 * client-side navigation is reported as a full absolute URL while the first
 * load of the same page is reported as one too — and either way the host, the
 * scheme and any stray parameter ride along into a key that is supposed to be a
 * page. Everything this app sends goes through here first, including the events
 * the tracker sends on its own, which is what the tool's own `before-send` hook
 * is for.
 */
export function toMeasurementPath(rawUrl: string, origin?: string): string {
  const base = typeof origin === "string" && origin !== "" ? origin : "http://localhost";
  let url: URL;
  try {
    url = new URL(String(rawUrl ?? ""), base);
  } catch {
    return "/";
  }
  const path = url.pathname === "" ? "/" : url.pathname;
  if (QUERY_STRIPPED_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`))) {
    return path;
  }
  const kept = new URLSearchParams();
  for (const name of KEPT_QUERY_PARAMETERS) {
    const value = url.searchParams.get(name);
    if (value !== null && value !== "") kept.set(name, value.slice(0, 120));
  }
  const query = kept.toString();
  return query === "" ? path : `${path}?${query}`;
}

/**
 * The attribute a custom event is named by, and the shape of the name.
 *
 * `area.element.action` — three lowercase segments — so a name is a coordinate
 * in a taxonomy rather than a sentence somebody wrote once. A name that does
 * not fit is not sent: an event nobody can find in a report is worse than no
 * event, because it looks like coverage.
 */
export const EVENT_ATTRIBUTE = "data-analytics-id";

const EVENT_NAME = /^[a-z0-9]+(?:-[a-z0-9]+)*(\.[a-z0-9]+(?:-[a-z0-9]+)*){2}$/;

export function isEventName(value: string | null | undefined): boolean {
  return typeof value === "string" && value.length <= 60 && EVENT_NAME.test(value);
}

/**
 * The event name for a clicked element, walking up to the nearest ancestor that
 * carries one — so a click on the label inside a button is the button's event.
 *
 * Deliberately takes only the name. There is no second argument for "and the
 * value", and that absence is the design: the one thing a browser tracker on
 * this product must never carry is a field of somebody's document, and the way
 * to guarantee it is to have no route by which a value could travel.
 */
export function eventNameForTarget(
  target: {
    closest(selector: string): { getAttribute(name: string): string | null } | null;
  } | null,
): string | null {
  const holder = target?.closest(`[${EVENT_ATTRIBUTE}]`) ?? null;
  const name = holder?.getAttribute(EVENT_ATTRIBUTE) ?? null;
  return isEventName(name) ? name : null;
}
